import React, { useEffect, useState } from 'react';
import { Snackbar, Button, Alert, IconButton, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddToHomeScreenIcon from '@mui/icons-material/AddToHomeScreen';
import IosShareIcon from '@mui/icons-material/IosShare';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if it's iOS
    const isIosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    
    // --- DEBUG: Allow forcing iOS mode via URL parameter for testing ---
    const urlParams = new URLSearchParams(window.location.search);
    const forceIOS = urlParams.get('force_ios') === 'true';
    
    const effectiveIsIOS = isIosDevice || forceIOS;
    setIsIOS(effectiveIsIOS);

    if (effectiveIsIOS) {
      // Check if already installed (standalone mode)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
      if ((!isStandalone || forceIOS) && !sessionStorage.getItem('installPromptDismissed')) {
        setShowPrompt(true);
      }
    }

    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later.
      setDeferredPrompt(e);
      // Update UI notify the user they can install the PWA
      // Only show if not already dismissed in this session
      if (!sessionStorage.getItem('installPromptDismissed')) {
         setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    // We no longer need the prompt.  Clear it
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowPrompt(false);
    sessionStorage.setItem('installPromptDismissed', 'true');
  };

  if (!showPrompt) return null;

  return (
    <Snackbar
      open={showPrompt}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: { xs: 90, sm: 24 } }} // Adjust bottom position to avoid covering nav/fab
    >
      <Alert
        onClose={handleClose}
        severity="info"
        icon={<AddToHomeScreenIcon fontSize="inherit" />}
        sx={{ 
          width: '100%', 
          bgcolor: 'primary.main', 
          color: 'white',
          '& .MuiAlert-icon': { color: 'white' }
        }}
        action={
          <>
            {!isIOS && (
              <Button color="inherit" size="small" onClick={handleInstallClick} sx={{ fontWeight: 'bold', border: '1px solid white', mr: 1 }}>
                INSTALL
              </Button>
            )}
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </>
        }
      >
        {isIOS ? (
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Tap <IosShareIcon fontSize="small" /> then "Add to Home Screen" to install app for better experience!
          </Box>
        ) : (
          "Install app for better experience!"
        )}
      </Alert>
    </Snackbar>
  );
};

export default InstallPrompt;
